import { useState, useEffect, useCallback } from 'react';
import { Contract, formatUnits, ZeroAddress } from 'ethers';
import type { JsonRpcSigner, BrowserProvider } from 'ethers';
import { ERC20_ABI } from '../constants/abis';

export interface TokenBalanceState {
  balance: bigint;
  formatted: string;
  allowance: bigint;
  loading: boolean;
  error: string | null;
}

export function useTokenBalance(
  tokenAddress: string | null,
  userAddress: string | null,
  spenderAddress: string | null,
  provider: BrowserProvider | null,
  decimals = 18
): TokenBalanceState & { refresh: () => void } {
  const [state, setState] = useState<TokenBalanceState>({
    balance: 0n,
    formatted: '0',
    allowance: 0n,
    loading: false,
    error: null,
  });

  const fetch = useCallback(async () => {
    if (!tokenAddress || !userAddress || !provider || tokenAddress === ZeroAddress) {
      setState(s => ({ ...s, loading: false }));
      return;
    }
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const contract = new Contract(tokenAddress, ERC20_ABI, provider);
      const [balance, allowance] = await Promise.all([
        contract.balanceOf(userAddress) as Promise<bigint>,
        spenderAddress
          ? (contract.allowance(userAddress, spenderAddress) as Promise<bigint>)
          : Promise.resolve(0n),
      ]);
      setState({
        balance,
        formatted: formatUnits(balance, decimals),
        allowance,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState(s => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch balance',
      }));
    }
  }, [tokenAddress, userAddress, spenderAddress, provider, decimals]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { ...state, refresh: fetch };
}

export async function approveToken(
  tokenAddress: string,
  spenderAddress: string,
  amount: bigint,
  signer: JsonRpcSigner
): Promise<void> {
  const contract = new Contract(tokenAddress, ERC20_ABI, signer);
  const tx = await contract.approve(spenderAddress, amount);
  await tx.wait();
}

export async function getTokenMeta(
  tokenAddress: string,
  provider: BrowserProvider
): Promise<{ symbol: string; name: string; decimals: number } | null> {
  try {
    const contract = new Contract(tokenAddress, ERC20_ABI, provider);
    const [symbol, name, decimals] = await Promise.all([
      contract.symbol() as Promise<string>,
      contract.name() as Promise<string>,
      contract.decimals() as Promise<bigint>,
    ]);
    return { symbol, name, decimals: Number(decimals) };
  } catch {
    return null;
  }
}
